import { useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { MdOutlineLockOpen, MdBookmarkBorder, MdCheckCircle, MdOutlineHolidayVillage, MdOutlinePeopleAlt } from 'react-icons/md';
import { AiOutlineMessage } from 'react-icons/ai';

import { selectLoading } from '../../reducers/app.reducer';
import { selectActiveAccount, selectImpersonated, selectUser, updateUser } from '../../reducers/account.reducer';

import { ConformityCard } from '../conformityCard/ConformityCard';
import { Button } from '../Button';
import { Counter } from '../counter/Counter';
import { COLORS, Z_INDEX } from '../../utils/constants';
import { EntityTable } from '../entity-table/EntityTable';
import { Modal } from '../modal/Modal';
import Calendar from '../calendar/Calendar';

import { getAccountConformityPercentage, getAccountCounters, getAccountProductAmount, getCompletedEntityAmount } from '../../utils/utils';

export interface DashboardGroupProps {}

const DashboardGroup = () => {
  //REDUX
  const dispatch = useDispatch<any>();

  const activeAccount = useSelector(selectActiveAccount);
  const impersonated = useSelector(selectImpersonated);
  const currentUser = useSelector(selectUser);
  const loading = useSelector(selectLoading);

  const userDetails = activeAccount?.users.find(user => user._id === currentUser._id);

  const [displayNewVersionModal, setDisplayNewVersionModal] = useState(userDetails?.displayNewVersionMsg);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const handleCloseVersionMsgModal = async () => {
    const accountCopy = _.cloneDeep(activeAccount);
    let userCopy = accountCopy.users.find(userItem => userItem._id === currentUser._id);
    userCopy.displayNewVersionMsg = false;
    dispatch(updateUser({ userId: userCopy._id, data: userCopy }));
    setDisplayNewVersionModal(false);
  };

  const groupCardData = {
    title: `Niveau de conformité de ${activeAccount?.companyName}`,
    data: [
      {
        icon: MdOutlineHolidayVillage,
        label: `${activeAccount?.entities?.length} entité${activeAccount?.entities?.length > 1 ? 's' : ''}`,
      },
      {
        icon: MdOutlinePeopleAlt,
        label: `${activeAccount?.users?.length} collaborateur${activeAccount?.users?.length > 1 ? 's' : ''}`,
      },
      {
        icon: MdBookmarkBorder,
        label: `${getAccountProductAmount(activeAccount)} produit${getAccountProductAmount(activeAccount) > 1 ? 's' : ''}`,
      },
      {
        icon: MdCheckCircle,
        label: `${getCompletedEntityAmount(activeAccount)} entité${getCompletedEntityAmount(activeAccount) > 1 ? 's' : ''} conforme${
          getCompletedEntityAmount(activeAccount) > 1 ? 's' : ''
        }`,
        color: COLORS.AquaMarine,
      },
    ],
    percentage: getAccountConformityPercentage(activeAccount),
  };

  const handleMeetingDPO = () => {
    setIsCalendarOpen(prevState => !prevState);
  };

  return (
    <DashboardGroupContainer>
      <DashboardTop>
        <Left>
          <Title>
            <span>{activeAccount?.companyName}</span>
          </Title>
          <Description
            dangerouslySetInnerHTML={{
              __html:
                'Bonjour et bienvenue sur votre tableau de bord !<br>Cet espace vous permettra de gérer vos entités et suivre votre conformité RGPD. Accédez par la suite aux questionnaires disponibles pour lancer votre aventure Dipeeo !',
            }}></Description>
          {currentUser?.role !== 'guest' && <Button onClick={handleMeetingDPO} label={'Prendre rendez-vous avec mon DPO'} />}
        </Left>
        <Right>
          <ConformityCard {...groupCardData} />
          {activeAccount && (
            <Counters>
              {getAccountCounters(activeAccount).dataViolation > 0 && (
                <Counter amount={getAccountCounters(activeAccount).dataViolation} icon={MdOutlineLockOpen} description={'Violations de données'} />
              )}
              {getAccountCounters(activeAccount).assistanceRequests > 0 && (
                <Counter
                  amount={getAccountCounters(activeAccount).assistanceRequests}
                  icon={AiOutlineMessage}
                  description={'Contacts avec les juristes de Dipeeo'}
                />
              )}
            </Counters>
          )}
        </Right>
      </DashboardTop>
      <DashboardContent>
        <EntityTable />
      </DashboardContent>

      {!loading && !impersonated && (
        <Modal
          alignment={'center'}
          size={{ width: '50%' }}
          show={displayNewVersionModal}
          title={`L’application Dipeeo fait peau neuve 🎉`}
          onClose={handleCloseVersionMsgModal}
          overAll>
          <BasicModalContainer>
            <p>Chers utilisateurs,</p>
            <p>
              Nous sommes ravis de vous annoncer la sortie de la toute nouvelle version de l’application Dipeeo ! Voici un aperçu des nouvelles
              fonctionnalités et améliorations que nous vous avons concocté :
            </p>
            <p>
              🖌️ <strong>Changement de design</strong> :<br />
              Bénéficiez d’un meilleur accès aux informations qui comptent le plus pour vous et trouvez ce que vous cherchez en un clin d’oeil.
            </p>
            <p>
              📈 <strong>Tableau de bord de conformité</strong> :<br />
              Suivez facilement le niveau de conformité de votre(s) entité(s) grâce à notre nouveau tableau de bord.
            </p>
            <p>
              🤝 <strong>Espace collaborateurs</strong> :<br />
              Nous avons ajouté une fonctionnalité très demandée : votre espace collaborateurs. Désormais, les gestionnaires de compte auront la
              possibilité de partager des questions spécifiques aux différents services concernés, tels que le marketing, les ressources humaines, et
              bien d'autres.
            </p>
            <p>Encore une fois et au nom de toute l'équipe Dipeeo : merci (mille fois) pour votre confiance au quotidien !😊</p>
            <p>À bientôt !</p>
          </BasicModalContainer>
        </Modal>
      )}

      {isCalendarOpen && <Calendar isCalendarOpen={isCalendarOpen} setIsCalendarOpen={setIsCalendarOpen} activeAccount={activeAccount} />}
    </DashboardGroupContainer>
  );
};

export default DashboardGroup;

const DashboardGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 110px 150px;
  row-gap: 50px;
  height: calc(100% - 220px);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 40%;
    bottom: 0;
    background-color: #c8e3f6;
    opacity: 30%;
    z-index: ${Z_INDEX.below};
  }
`;

const DashboardTop = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 45px;
  flex: 1;

  @media (max-width: 1260px) {
    display: none;
  }
`;

const Right = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  column-gap: 30px;

  @media (max-width: 1260px) {
    justify-content: center;
  }
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 50px;
  display: flex;
  align-items: center;
  column-gap: 20px;

  svg {
    width: 25px;
    height: 25px;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s ease-in-out;

    &:hover {
      opacity: 1;
    }
  }
`;

const Description = styled.div`
  font-size: 16px;
  font-weight: 400;
  text-align: justify;
  width: 85%;
  line-height: 2rem;
`;

const DashboardContent = styled.div`
  flex: 1;
  min-height: 0;
`;

const Counters = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  row-gap: 20px;

  @media (max-width: 1510px) {
    display: none;
  }
`;

const BasicModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  column-gap: 20px;
  overflow-y: auto;

  p {
    font-size: 1.3em;
    font-weight: 400;
    font-family: 'Roboto', serif;
    line-height: 1.5em;
  }
`;
